

// /**
//  * adapted from http://stemkoski.github.io/Three.js/Sprite-Text-Labels.html
//  */

import * as THREE from "../libs/three.js/build/three.module.js";

export class TextSprite extends THREE.Object3D{

	constructor(text){
		super();

		let texture = new THREE.Texture();
		texture.minFilter = THREE.LinearFilter;
		texture.magFilter = THREE.LinearFilter;
		let spriteMaterial = new THREE.SpriteMaterial({
			map: texture,
			depthTest: false,
			depthWrite: false});

		this.texture = texture;

		this.material = spriteMaterial;
		//this.material = getRawMaterial(texture);
		this.sprite = new THREE.Sprite(this.material);
		// Labels already opt out of the depth buffer entirely (depthTest/
		// depthWrite: false, above) so they can't be depth-occluded - but
		// three.js still sorts same-renderOrder TRANSPARENT objects by an
		// approximate camera-distance heuristic, and a label's own position
		// can sort "behind" a large semi-transparent surface mesh whose
		// distance is judged from its geometry's bounding-sphere centre
		// rather than the actual pixels under the label. When that happens,
		// the surface (also transparent) draws AFTER the label and its tint
		// blends on top, making the label look faint/"under" the surface -
		// reported directly against "Areal og volumen"'s result label
		// sitting on its TIN. A high fixed renderOrder sidesteps the
		// distance-sort ambiguity entirely: every label always draws last
		// (on top), regardless of which surface meshes happen to be visible
		// underneath it - not just volume's, any tool's labels.
		this.sprite.renderOrder = 10;
		this.add(this.sprite);

		this.borderThickness = 4;
		this.fontface = 'Arial';
		this.fontsize = 28;
		this.borderColor = { r: 0, g: 0, b: 0, a: 1.0 };
		this.backgroundColor = { r: 255, g: 255, b: 255, a: 1.0 };
		this.textColor = {r: 255, g: 255, b: 255, a: 1.0};
		this.text = '';
		// ekstra plads til højre i selve label-boksen (canvas-px), fx til et kopi-ikon i hjørnet af en kort, enkeltlinjet tekst
		this.extraRight = 0;

		// Teksturer for de seneste tekster (se update()): en label, der skifter mellem to tekster (fx fuld/kompakt),
		// tegner ikke canvas og tekstur forfra hver gang, og gamle teksturer frigives i stedet for at ligge i GPU-hukommelsen.
		this._textureCache = new Map();

		this.setText(text);
	}

	setText(text, extraRight = this.extraRight){
		if (this.text !== text || this.extraRight !== extraRight){
			this.text = text;
			this.extraRight = extraRight;

			this.update();
		}
	}

	setTextColor(color){
		this.textColor = color;

		this.update();
	}

	setBorderColor(color){
		this.borderColor = color;

		this.update();
	}

	setBackgroundColor(color){
		this.backgroundColor = color;

		this.update();
	}

	// Sætter stil og skriftstørrelse i ét hug og tegner én gang (setBackgroundColor/setBorderColor/setTextColor tegner hver for sig).
	configure({background, border, text, fontsize} = {}){
		if(background) this.backgroundColor = background;
		if(border) this.borderColor = border;
		if(text) this.textColor = text;
		if(fontsize) this.fontsize = fontsize;

		this.update();
	}

	// Størrelsen (px i tekstur-canvas) en tekst får - samme regnestykke som update(), uden at tegne. Bruges til at afgøre,
	// om en label passer på en plads, før den tegnes. Resultatet caches pr. tekst/størrelse.
	static measure(text, fontsize = 28, borderThickness = 4, fontface = 'Arial', extraRight = 0){
		const key = fontsize + '|' + borderThickness + '|' + fontface + '|' + extraRight + '|' + text;
		let hit = TextSprite._measureCache.get(key);
		if(hit) return hit;

		if(!TextSprite._measureContext){
			TextSprite._measureContext = document.createElement('canvas').getContext('2d');
		}
		const context = TextSprite._measureContext;
		context.font = 'Bold ' + fontsize + 'px ' + fontface;

		const lines = text.split('\n');
		let textWidth = 0;
		for(const line of lines){
			textWidth = Math.max(textWidth, context.measureText(line).width);
		}

		const lineHeight = fontsize * 1.4;
		hit = {
			width: 2 * 5 + textWidth + 2 * borderThickness + extraRight,
			height: lineHeight * lines.length + 2 * borderThickness,
			textWidth,
			lineHeight,
		};
		if(TextSprite._measureCache.size > 400) TextSprite._measureCache.clear();
		TextSprite._measureCache.set(key, hit);
		return hit;
	}

	// Frigiver alle GPU-ressourcer (materiale og alle cachede teksturer).
	dispose(){
		this.material.dispose();
		for(const entry of this._textureCache.values()) entry.texture.dispose();
		this._textureCache.clear();
		if(this.texture) this.texture.dispose();
	}

	update(){
		const c = this.backgroundColor, b = this.borderColor, tc = this.textColor;
		const cacheKey = this.fontsize + '|' + this.borderThickness + '|' + this.fontface + '|'
			+ c.r + ',' + c.g + ',' + c.b + ',' + c.a + '|' + b.r + ',' + b.g + ',' + b.b + ',' + b.a + '|'
			+ tc.r + ',' + tc.g + ',' + tc.b + ',' + tc.a + '|' + this.extraRight + '|' + this.text;
		const cached = this._textureCache.get(cacheKey);
		if(cached){
			// seneste brug sidst i Map'en
			this._textureCache.delete(cacheKey);
			this._textureCache.set(cacheKey, cached);
			this._applyTexture(cached);
			return;
		}

		let canvas = document.createElement('canvas');
		let context = canvas.getContext('2d');
		context.font = 'Bold ' + this.fontsize + 'px ' + this.fontface;

		// text may contain '\n'-separated lines, stacked top to bottom;
		// a single line reduces to the exact same geometry as before
		let lines = this.text.split('\n');
		let lineHeight = this.fontsize * 1.4;

		let textWidth = 0;
		for(let line of lines){
			textWidth = Math.max(textWidth, context.measureText(line).width);
		}

		let margin = 5;
		let spriteWidth = 2 * margin + textWidth + 2 * this.borderThickness + this.extraRight;
		let spriteHeight = lineHeight * lines.length + 2 * this.borderThickness;

		context.canvas.width = spriteWidth;
		context.canvas.height = spriteHeight;
		context.font = 'Bold ' + this.fontsize + 'px ' + this.fontface;

		// background color
		context.fillStyle = 'rgba(' + this.backgroundColor.r + ',' + this.backgroundColor.g + ',' +
			this.backgroundColor.b + ',' + this.backgroundColor.a + ')';
		// border color
		context.strokeStyle = 'rgba(' + this.borderColor.r + ',' + this.borderColor.g + ',' +
			this.borderColor.b + ',' + this.borderColor.a + ')';

		context.lineWidth = this.borderThickness;
		this.roundRect(context, this.borderThickness / 2, this.borderThickness / 2,
			textWidth + this.borderThickness + 2 * margin + this.extraRight, lineHeight * lines.length + this.borderThickness, 6);

		// the border rectangle above set lineWidth to borderThickness (a few
		// px) for its own stroke - reset to a thin outline here, otherwise
		// every line's black text-outline below inherits that thick width
		// and can swallow the white fill entirely at small font sizes,
		// reading as a solid black blob rather than legible text
		context.lineWidth = 1;

		for(let i = 0; i < lines.length; i++){
			let baselineY = lineHeight * i + this.fontsize + this.borderThickness;

			// text color
			context.strokeStyle = 'rgba(0, 0, 0, 1.0)';
			context.strokeText(lines[i], this.borderThickness + margin, baselineY);

			context.fillStyle = 'rgba(' + this.textColor.r + ',' + this.textColor.g + ',' +
				this.textColor.b + ',' + this.textColor.a + ')';
			context.fillText(lines[i], this.borderThickness + margin, baselineY);
		}

		let texture = new THREE.Texture(canvas);
		texture.minFilter = THREE.LinearFilter;
		texture.magFilter = THREE.LinearFilter;
		texture.needsUpdate = true;
		//this.material.needsUpdate = true;

		// { // screen-space sprite
		// 	let [screenWidth, screenHeight] = [1620, 937];

		// 	let uniforms = this.sprite.material.uniforms;
		// 	let aspect = spriteHeight / spriteWidth;
		// 	let factor = 0.5;

		// 	let w = spriteWidth / screenWidth;
		// 	let h = spriteHeight / screenHeight;

		// 	uniforms.uScale.value = [2 * w, 2 * h];
		// 	//uniforms.uScale.value = [factor * 1, factor * aspect];
		//	this.sprite.material.uniforms.map.value = texture;
		// }

		const entry = {texture, width: spriteWidth, height: spriteHeight};
		this._textureCache.set(cacheKey, entry);
		this._applyTexture(entry);

		// kun den nuværende og den forrige tekstur beholdes; resten frigives
		while(this._textureCache.size > 2){
			const oldestKey = this._textureCache.keys().next().value;
			this._textureCache.get(oldestKey).texture.dispose();
			this._textureCache.delete(oldestKey);
		}
	}

	_applyTexture(entry){
		const previous = this.texture;
		this.sprite.material.map = entry.texture;
		this.texture = entry.texture;
		// den tomme start-tekstur fra konstruktøren hører ikke til cachen
		if(previous && previous !== entry.texture && !this._isCached(previous)) previous.dispose();

		this.sprite.scale.set(entry.width * 0.01, entry.height * 0.01, 1.0);
	}

	_isCached(texture){
		for(const entry of this._textureCache.values()){
			if(entry.texture === texture) return true;
		}
		return false;
	}

	roundRect(ctx, x, y, w, h, r){
		ctx.beginPath();
		ctx.moveTo(x + r, y);
		ctx.lineTo(x + w - r, y);
		ctx.quadraticCurveTo(x + w, y, x + w, y + r);
		ctx.lineTo(x + w, y + h - r);
		ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
		ctx.lineTo(x + r, y + h);
		ctx.quadraticCurveTo(x, y + h, x, y + h - r);
		ctx.lineTo(x, y + r);
		ctx.quadraticCurveTo(x, y, x + r, y);
		ctx.closePath();
		ctx.fill();
		ctx.stroke();
	}

}

TextSprite._measureCache = new Map();
TextSprite._measureContext = null;

